import { Injectable, signal, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {finalize, map, Observable, of, shareReplay, tap} from 'rxjs';
import {environment} from "../../environments/environment.development";
import {controllersNames} from "../controllersNames";
import {PaletteService} from "./palette.service";

/** #848: контекст капчи формы из api/Settings — какой виджет рисовать. */
export interface CaptchaContext {
  /** 'google' | 'yandex' | '' (выкл — форма без капчи) */
  provider: string;
  siteKey: string;
  jsUrl: string;
}

export interface SiteSettings {
  coursesEnabled: boolean;
  /** имена пресетов палитр из tailwind.css; '' = дефолтная палитра */
  paletteLight: string;
  paletteDark: string;
  /** #784: включённые OAuth-провайдеры входа ('yandex' | 'vk'); пусто — кнопок нет */
  authProviders: string[];
  /** #848: капча регистрации; provider '' — виджета нет */
  registerCaptcha?: CaptchaContext;
}

/** Публичные настройки сайта (api/Settings): флаги разделов + палитры темы. */
@Injectable({providedIn: 'root'})
export class SettingsService {
  private http = inject(HttpClient);
  private palette = inject(PaletteService);

  private readonly api = environment.apiBase + '/' + controllersNames.settings;

  /** null — флаг ещё не загружался */
  coursesEnabled = signal<boolean | null>(null);
  /** #784: OAuth-провайдеры из api/Settings; пусто — вход только по паролю */
  authProviders = signal<string[]>([]);
  /** #848: контекст капчи регистрации; null — не загружено */
  registerCaptcha = signal<CaptchaContext | null>(null);
  private loaded = false;
  /** Request in flight: app shell, header and pages call load() at once — one HTTP call for all (#1173). */
  private inflight?: Observable<SiteSettings>;

  /** Тянет настройки один раз; повторные вызовы — кеш из signal. */
  load(): Observable<SiteSettings> {
    if (this.loaded) {
      return of({
        coursesEnabled: this.coursesEnabled() === true,
        paletteLight: this.palette.siteLight(),
        paletteDark: this.palette.siteDark(),
        authProviders: this.authProviders(),
        registerCaptcha: this.registerCaptcha() ?? undefined,
      });
    }
    if (this.inflight) {
      return this.inflight;
    }
    this.inflight = this.http.get<SiteSettings>(this.api).pipe(
      map(v => ({...v, paletteLight: v.paletteLight ?? '', paletteDark: v.paletteDark ?? '', authProviders: v.authProviders ?? []})),
      tap(v => {
        this.loaded = true;
        this.coursesEnabled.set(v.coursesEnabled);
        this.authProviders.set(v.authProviders);
        this.registerCaptcha.set(v.registerCaptcha ?? null);
        // сайт-палитры применяются к <html>; в SSR атрибуты уезжают в HTML
        this.palette.applySite(v.paletteLight, v.paletteDark);
      }),
      // on error the next load() asks again; after success `loaded` serves it from signals
      finalize(() => this.inflight = undefined),
      shareReplay({bufferSize: 1, refCount: false})
    );
    return this.inflight;
  }

  setCoursesEnabled(enabled: boolean): Observable<{ coursesEnabled: boolean }> {
    return this.http.put<{ coursesEnabled: boolean }>(
      `${this.api}/courses-enabled`, {enabled}).pipe(
      tap(v => {
        this.loaded = true;
        this.coursesEnabled.set(v.coursesEnabled);
      })
    );
  }

  /** Админ: сохранить сайт-палитры темы (ключи theme.palette.* на бэке). */
  setPalettes(paletteLight: string, paletteDark: string): Observable<void> {
    return this.http.put<void>(`${this.api}/palettes`, {
      'theme.palette.light': paletteLight,
      'theme.palette.dark': paletteDark
    }).pipe(
      tap(() => this.palette.applySite(paletteLight, paletteDark))
    );
  }

  /** Админ: все ключи оплаты (payments.*) — пустое значение = метод выкл. */
  getPayments(): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(`${this.api}/payments`);
  }

  /** Админ: сохранить ключи оплаты (только белый список на бэке). */
  setPayments(values: Record<string, string>): Observable<void> {
    return this.http.put<void>(`${this.api}/payments`, values);
  }
}
